import { useState, useEffect, useRef } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi } from "@/lib/api";
import type { AttemptRecord } from "@/lib/types";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

const OUTCOME_COLORS: Record<string, string> = {
  success: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  retry: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  terminal: "text-red-400 bg-red-500/10 border-red-500/20",
  budget_exceeded: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  client_disconnected: "text-sky-400 bg-sky-500/10 border-sky-500/20",
};

const OUTCOME_LABELS: Record<string, string> = {
  success: "Success",
  retry: "Retry",
  terminal: "Terminal",
  budget_exceeded: "Budget Exceeded",
  client_disconnected: "Client Disconnected",
};

export default function AdminUsageAttempts() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [records, setRecords] = useState<AttemptRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Defaults: from 24h ago to now
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  const [dateError, setDateError] = useState("");
  const [loadError, setLoadError] = useState("");

  const [teamFilter, setTeamFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [upstreamStatusFilter, setUpstreamStatusFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");

  const [appliedTeamFilter, setAppliedTeamFilter] = useState("");
  const [appliedProviderFilter, setAppliedProviderFilter] = useState("");
  const [appliedUpstreamStatusFilter, setAppliedUpstreamStatusFilter] = useState("");

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 50;

  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [justQueried, setJustQueried] = useState(false);
  const justQueriedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyFilters = () => {
    if (justQueried) return; // 对号显示期间忽略重复点击/回车
    setAppliedTeamFilter(teamFilter);
    setAppliedProviderFilter(providerFilter);
    setAppliedUpstreamStatusFilter(upstreamStatusFilter);
    setPage(0);
    setJustQueried(true);
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
    justQueriedTimer.current = setTimeout(() => setJustQueried(false), 1200);
  };

  // Clear the pending 对号 timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (justQueriedTimer.current) clearTimeout(justQueriedTimer.current);
  }, []);

  const fetchLogs = async () => {
    if (!user?.is_system_admin) return;

    setDateError("");
    const fromTime = new Date(startDate).getTime();
    const toTime = new Date(endDate + "T23:59:59").getTime();
    if (toTime - fromTime > 7 * 24 * 60 * 60 * 1000) {
      setDateError(t("logs.admin.errorMax7Days") || "Date range cannot exceed 7 days");
      return;
    }

    setLoading(true);
    setLoadError("");
    try {
      const usage = await adminApi.listUsageAttempts({
        page_size: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        team_id: appliedTeamFilter || undefined,
        provider: appliedProviderFilter || undefined,
        outcome: outcomeFilter || undefined,
        upstream_status: appliedUpstreamStatusFilter ? parseInt(appliedUpstreamStatusFilter, 10) : undefined,
        from: new Date(startDate).toISOString(),
        to: new Date(endDate + "T23:59:59").toISOString(),
      });
      setRecords(usage.records);
      setTotal(usage.total);
    } catch (err) {
      setRecords([]);
      setTotal(0);
      setLoadError(err instanceof Error ? err.message : t("logs.loadFailed") || "Failed to load attempts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_system_admin, page, appliedTeamFilter, appliedProviderFilter, appliedUpstreamStatusFilter, outcomeFilter, startDate, endDate]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <ConsoleLayout
      title={t("logs.admin.attemptsTitle") || "Usage Attempts"}
      subtitle={t("logs.admin.attemptsSubtitle") || "View and debug random failover attempt chains"}
    >
      <div className="p-6 space-y-5">
        {/* Filters */}
        <div className="flex flex-col gap-2">
          {dateError && <div className="text-red-400 text-xs">{dateError}</div>}
          {loadError && <div className="text-red-400 text-xs">{loadError}</div>}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <DatePicker value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }} />
              <span className="text-xs text-slate-600">—</span>
              <DatePicker value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }} />
            </div>

            <select
              value={outcomeFilter}
              onChange={(e) => { setOutcomeFilter(e.target.value); setPage(0); }}
              className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
            >
              <option value="">{t("logs.admin.allOutcomes") || "All Outcomes"}</option>
              {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <Input
              placeholder={t("logs.admin.filterByTeam") || "Team ID"}
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 w-[140px] font-mono"
            />
            <Input
              placeholder={t("logs.admin.filterByProvider") || "Provider Slug"}
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 w-[140px] font-mono"
            />
            <Input
              placeholder={t("logs.admin.filterByStatus") || "Upstream Status"}
              value={upstreamStatusFilter}
              onChange={(e) => setUpstreamStatusFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              className="bg-white/5 border-white/10 text-xs h-9 text-white placeholder:text-slate-600 w-[140px] font-mono"
            />

            <Button size="sm" variant="ghost" onClick={applyFilters} className="relative h-9 px-3 text-xs text-slate-300 border border-white/10 active:bg-white/10">
              <span className={justQueried ? "invisible" : undefined}>{t("logs.applyFilters")}</span>
              {justQueried && (
                <span className="absolute inset-0 flex items-center justify-center text-emerald-400">
                  <Check size={14} />
                </span>
              )}
            </Button>
            
            <div className="ml-auto flex items-center gap-3">
              <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[100px_1fr_120px_140px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div>Outcome</div>
            <div>Request ID / Team</div>
            <div>Provider</div>
            <div className="text-right">Time / Latency</div>
            <div className="text-right">Upstream</div>
            <div />
          </div>
          {records.length > 0 ? (
            records.map((log) => (
              <AttemptRowItem key={log.id} log={log} expanded={expandedRow === log.request_id} setExpandedRow={setExpandedRow} />
            ))
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}

function AttemptRowItem({ log, expanded, setExpandedRow }: { log: AttemptRecord, expanded: boolean, setExpandedRow: (id: string | null) => void }) {
  const [chain, setChain] = useState<AttemptRecord[] | null>(null);
  const [loadingChain, setLoadingChain] = useState(false);
  const [chainError, setChainError] = useState("");
  const [errorExpand, setErrorExpand] = useState<Record<string, boolean>>({});

  const toggleExpand = async () => {
    if (expanded) {
      setExpandedRow(null);
    } else {
      setExpandedRow(log.request_id);
      if (!chain) {
        setLoadingChain(true);
        setChainError("");
        try {
          const detail = await adminApi.getAttemptChain(log.request_id);
          setChain(detail.records);
        } catch (err) {
          setChainError(err instanceof Error ? err.message : "Failed to load attempt chain");
        } finally {
          setLoadingChain(false);
        }
      }
    }
  };

  return (
    <div className="border-b border-white/5 last:border-0 group transition-colors">
      <div 
        className="px-5 py-3 grid grid-cols-[100px_1fr_120px_140px_80px_20px] gap-4 items-center cursor-pointer hover:bg-white/[0.02]"
        onClick={toggleExpand}
      >
        <div>
          <span className={cn("px-2 py-0.5 rounded text-[10px] border", OUTCOME_COLORS[log.attempt_outcome] || "text-slate-400 border-white/10")}>
            {OUTCOME_LABELS[log.attempt_outcome] || log.attempt_outcome}
          </span>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-white font-mono truncate">{log.request_id}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-0.5">{log.team_id}</div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-300 truncate">{log.provider || "—"}</div>
          <div className="text-[10px] text-slate-500 truncate mt-0.5">{log.upstream_model || log.model_key || "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">{new Date(log.started_at).toLocaleString()}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{log.latency_ms ? `${log.latency_ms}ms` : "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-mono text-slate-400">{log.http_status || "—"}</div>
        </div>
        <div>
          {expanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        </div>
      </div>
      
      {expanded && (
        <div className="bg-black/20 p-5 border-t border-white/5">
          <h4 className="text-xs font-medium text-slate-300 mb-3">Attempt Chain</h4>
          {loadingChain && !chain ? (
            <div className="text-xs text-slate-500">Loading chain...</div>
          ) : chainError ? (
            <div className="text-xs text-red-400">{chainError}</div>
          ) : chain ? (
            <div className="space-y-2">
              {chain.map((attempt) => (
                <div key={attempt.id} className="bg-white/5 rounded border border-white/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 bg-white/10 px-1.5 py-0.5 rounded">
                        #{attempt.attempt_index}
                      </span>
                      <span className={cn("px-1.5 py-0.5 rounded text-[10px] border", OUTCOME_COLORS[attempt.attempt_outcome] || "text-slate-400 border-white/10")}>
                        {OUTCOME_LABELS[attempt.attempt_outcome] || attempt.attempt_outcome}
                      </span>
                      {attempt.is_final && (
                        <span className="text-[10px] text-blue-400 bg-blue-500/10 border border-blue-500/20 px-1.5 py-0.5 rounded">
                          final
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400">
                      {attempt.provider} &middot; {attempt.latency_ms ? `${attempt.latency_ms}ms` : "—"}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-slate-500">Provider Model: </span>
                      <span className="text-slate-300 font-mono">{attempt.upstream_model || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Status: </span>
                      <span className="text-slate-300 font-mono">{attempt.http_status || "—"}</span>
                    </div>
                  </div>

                  {(attempt.error_code || attempt.error_detail) && (
                    <div className="mt-2 border-t border-white/5 pt-2">
                      <div className="text-xs text-red-400 mb-1 font-mono">{attempt.error_code}</div>
                      {attempt.error_detail && (
                        <div 
                          className="cursor-pointer group/err"
                          onClick={() => setErrorExpand(prev => ({ ...prev, [attempt.id]: !prev[attempt.id] }))}
                        >
                          <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1 group-hover/err:text-slate-400">
                            {errorExpand[attempt.id] ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                            Error Detail
                          </div>
                          {errorExpand[attempt.id] && (
                            <pre className="text-[10px] text-red-300/80 bg-red-950/20 p-2 rounded whitespace-pre-wrap font-mono overflow-x-auto border border-red-900/30">
                              {attempt.error_detail}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500">No chain data available.</div>
          )}
        </div>
      )}
    </div>
  );
}
